import { FormEvent, useEffect, useMemo, useState } from 'react'
import { fetchAnnualOkrs, fetchGoals, fetchMeta, fetchTeam, saveAnnualOkrs, saveGoals, notifyWxwork, updateGoal } from '../utils/api'
import { getCurrentUser } from '../utils/auth'
import type { AnnualOKR, AnnualOKRPayload, GoalItem, GoalLevel, GoalStatus, MemberProfile, Priority } from '../types/okr'

const now = () => new Date().toISOString()
const currentMonth = () => new Date().toISOString().slice(0, 7)
const currentIsoWeek = () => {
  const date = new Date()
  const target = new Date(date.valueOf())
  const dayNr = (date.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = new Date(target.getFullYear(), 0, 4)
  const weekNumber = 1 + Math.round(((target.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7)
  return `${target.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`
}

const normalizePeriodValue = (period: string) => {
  const trimmed = (period || '').trim()
  const monthlyMatch = trimmed.match(/^(\d{4})-(\d{1,2})$/)
  if (monthlyMatch) {
    const [, y, m] = monthlyMatch
    return `${y}-${m.padStart(2, '0')}`
  }
  return trimmed
}

const emptyOkrForm = {
  id: '',
  objective: '',
  category: '',
  owner: '于建波',
  coOwner: '',
  coOwnerId: '',
  weight: 20,
  progress: 0,
  description: '',
  keyResultsText: '',
  source: '系统维护'
}

const emptyGoalForm = {
  id: '',
  title: '',
  description: '',
  level: 'monthly' as GoalLevel,
  period: '2026-03',
  owner: '于建波',
  ownerId: '',
  status: 'not_started' as GoalStatus,
  progress: 0,
  priority: 'medium' as Priority,
  dueDate: '',
  relatedOkrId: '',
  tags: '',
  nextStep: '',
  blockers: '',
  source: '系统维护'
}

function createId(prefix: string) {
  return `${prefix}-${Date.now()}`
}

type GoalsProps = {
  isTeamMember?: boolean
}

function Goals({ isTeamMember = false }: GoalsProps) {
  const currentUser = useMemo(() => getCurrentUser(), [])
  const [ownerId, setOwnerId] = useState('GS2480')
  const [annualData, setAnnualData] = useState<AnnualOKRPayload>({ year: 2026, okrs: [] })
  const [goals, setGoals] = useState<GoalItem[]>([])
  const [members, setMembers] = useState<MemberProfile[]>([])
  const [memberFilter, setMemberFilter] = useState('')
  const [okrForm, setOkrForm] = useState(emptyOkrForm)
  const [goalForm, setGoalForm] = useState(emptyGoalForm)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [okrDrawerOpen, setOkrDrawerOpen] = useState(false)
  const [goalDrawerOpen, setGoalDrawerOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'team' | 'mine'>('mine')
  const [updatingGoalId, setUpdatingGoalId] = useState('')
  const [monthlyPeriod, setMonthlyPeriod] = useState(currentMonth())
  const [weeklyPeriod, setWeeklyPeriod] = useState(currentIsoWeek())

  const isOwner = Boolean(currentUser.userId && ownerId && currentUser.userId.toLowerCase() === ownerId.toLowerCase())
  const canManageGoals = isOwner || isTeamMember
  const isMineValue = (owner: string) => owner === currentUser.name || owner === currentUser.userId
  const isMineGoal = (goal: GoalItem) => goal.ownerId === currentUser.userId || isMineValue(goal.owner)
  const isMyOkr = (okr: AnnualOKR) => {
    if (okr.ownerId && okr.ownerId.toLowerCase() === currentUser.userId.toLowerCase()) return true
    if (okr.coOwnerId && okr.coOwnerId.toLowerCase() === currentUser.userId.toLowerCase()) return true
    if (okr.owner === currentUser.name) return true
    if (okr.coOwner && okr.coOwner === currentUser.name) return true
    return false
  }

  useEffect(() => {
    const load = async () => {
      try {
        const [annual, goalList] = await Promise.all([fetchAnnualOkrs(), fetchGoals()])
        const normalizedGoals = goalList.map((g) => ({ ...g, period: normalizePeriodValue(g.period) }))
        setAnnualData(annual)
        setGoals(normalizedGoals)
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载目标数据失败')
      }
    }

    load()
  }, [])

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const meta = await fetchMeta()
        if (meta?.ownerId) setOwnerId(meta.ownerId)
      } catch {
        // 静默降级
      }
    }
    loadMeta()
  }, [])

  useEffect(() => {
    const loadTeam = async () => {
      try {
        const team = await fetchTeam()
        const normalized = [team.owner, ...(team.members || [])].filter(Boolean).reduce((acc: MemberProfile[], item) => {
          if (!acc.find((m) => m.userId === item.userId)) acc.push(item)
          return acc
        }, [])
        setMembers(normalized)
        if (!goalForm.ownerId && normalized.length > 0) {
          const defaultOwner = normalized.find((m) => m.userId.toLowerCase() === ownerId.toLowerCase()) || normalized[0]
          setGoalForm((prev) => ({ ...prev, ownerId: defaultOwner.userId, owner: defaultOwner.name }))
        }
        if (!okrForm.coOwnerId && normalized.length > 1) {
          const firstMember = normalized.find((m) => m.userId.toLowerCase() !== ownerId.toLowerCase()) || normalized[0]
          setOkrForm((prev) => ({ ...prev, coOwnerId: firstMember.userId, coOwner: firstMember.name }))
        }
      } catch {
        // 忽略失败，仍允许手填
      }
    }
    loadTeam()
  }, [ownerId, currentUser.userId, goalForm.ownerId, okrForm.coOwnerId])

  useEffect(() => {
    setViewMode(canManageGoals ? 'team' : 'mine')
  }, [isOwner])

  const monthlyPeriods = useMemo(() => {
    const periods = goals.filter((g) => g.level === 'monthly').map((g) => g.period).filter(Boolean)
    return Array.from(new Set(periods)).sort().reverse()
  }, [goals])

  const weeklyPeriods = useMemo(() => {
    const periods = goals.filter((g) => g.level === 'weekly').map((g) => g.period).filter(Boolean)
    return Array.from(new Set(periods)).sort().reverse()
  }, [goals])

  useEffect(() => {
    if (monthlyPeriods.length && !monthlyPeriods.includes(monthlyPeriod)) {
      setMonthlyPeriod(monthlyPeriods[0])
    }
  }, [monthlyPeriods, monthlyPeriod])

  useEffect(() => {
    if (weeklyPeriods.length && !weeklyPeriods.includes(weeklyPeriod)) {
      setWeeklyPeriod(weeklyPeriods[0])
    }
  }, [weeklyPeriods, weeklyPeriod])

  const filteredGoals = useMemo(() => {
    const base = viewMode === 'mine' ? goals.filter((g) => isMineGoal(g)) : goals
    const memberFiltered = memberFilter ? base.filter((g) => g.ownerId === memberFilter || g.owner === memberFilter) : base
    const monthlyFiltered = memberFiltered.filter((goal) => goal.level === 'monthly').filter((goal) => !monthlyPeriod || goal.period === monthlyPeriod)
    const weeklyFiltered = memberFiltered.filter((goal) => goal.level === 'weekly').filter((goal) => !weeklyPeriod || goal.period === weeklyPeriod)
    return {
      monthly: monthlyFiltered,
      weekly: weeklyFiltered
    }
  }, [goals, viewMode, currentUser, memberFilter, monthlyPeriod, weeklyPeriod])

  const visibleAnnualOkrs = useMemo(() => {
    const base = viewMode === 'mine' ? annualData.okrs.filter((okr) => isMyOkr(okr)) : annualData.okrs
    if (!memberFilter) return base
    return base.filter((okr) =>
      okr.ownerId === memberFilter || okr.owner === memberFilter || okr.coOwnerId === memberFilter || okr.coOwner === memberFilter
    )
  }, [annualData.okrs, viewMode, currentUser, memberFilter])

  const saveBoth = async (nextAnnual: AnnualOKRPayload, nextGoals: GoalItem[]) => {
    setSaving(true)
    setError('')
    setMessage('')
    try {
      await Promise.all([saveAnnualOkrs(nextAnnual), saveGoals(nextGoals)])
      setAnnualData(nextAnnual)
      setGoals(nextGoals)
      setMessage('已保存到系统')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败')
    } finally {
      setSaving(false)
    }
  }

  const handleOkrSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!isOwner) {
      setError('仅负责人可维护年度OKR')
      return
    }
    if (!okrForm.objective.trim()) return setError('目标主题必填')
    if (!okrForm.category.trim()) return setError('分类必填')

    const timestamp = now()
    const keyResults = okrForm.keyResultsText
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, index) => ({
        id: `${okrForm.id || 'kr'}-${index}-${Date.now()}`,
        title: item,
        target: item,
        current: '待更新',
        status: 'not_started' as GoalStatus
      }))

    const coOwnerProfile = okrForm.coOwnerId ? members.find((m) => m.userId === okrForm.coOwnerId) : null
    const payload: AnnualOKR = {
      id: okrForm.id || createId('okr'),
      objective: okrForm.objective,
      category: okrForm.category,
      owner: okrForm.owner,
      ownerId: ownerId,
      coOwner: coOwnerProfile?.name || okrForm.coOwner,
      coOwnerId: coOwnerProfile?.userId || okrForm.coOwnerId,
      weight: Number(okrForm.weight),
      progress: Math.min(100, Math.max(0, Number(okrForm.progress))),
      description: okrForm.description,
      keyResults,
      source: okrForm.source,
      createdAt: timestamp,
      updatedAt: timestamp
    }

    const nextOkrs = okrForm.id
      ? annualData.okrs.map((item) => item.id === okrForm.id ? { ...payload, createdAt: item.createdAt, updatedAt: timestamp } : item)
      : [payload, ...annualData.okrs]

    await saveBoth({ ...annualData, okrs: nextOkrs }, goals)
    setOkrForm(emptyOkrForm)
    setOkrDrawerOpen(false)
  }

  const handleGoalSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!canManageGoals) {
      setError('仅团队成员可维护执行目标')
      return
    }
    if (!goalForm.title.trim()) return setError('目标标题必填')
    if (!goalForm.period.trim()) return setError('周期必填')
    if (!goalForm.ownerId.trim()) return setError('责任人必选')
    if (goalForm.status === 'blocked' && !goalForm.blockers.trim()) return setError('阻塞说明必填')

    const timestamp = now()
    const ownerProfile = goalForm.ownerId ? members.find((m) => m.userId === goalForm.ownerId) : null
    const normalizedPeriod = normalizePeriodValue(goalForm.period)
    const payload: GoalItem = {
      id: goalForm.id || createId('goal'),
      title: goalForm.title,
      description: goalForm.description,
      level: goalForm.level,
      period: normalizedPeriod,
      owner: ownerProfile?.name || goalForm.owner,
      ownerId: ownerProfile?.userId || goalForm.ownerId,
      status: goalForm.status,
      progress: Math.min(100, Math.max(0, Number(goalForm.progress))),
      priority: goalForm.priority,
      dueDate: goalForm.dueDate,
      relatedOkrId: goalForm.relatedOkrId || undefined,
      tags: goalForm.tags.split(',').map((item) => item.trim()).filter(Boolean),
      nextStep: goalForm.nextStep,
      blockers: goalForm.blockers,
      source: goalForm.source,
      createdAt: timestamp,
      updatedAt: timestamp
    }

    const nextGoals = goalForm.id
      ? goals.map((item) => item.id === goalForm.id ? { ...payload, createdAt: item.createdAt, updatedAt: timestamp } : item)
      : [payload, ...goals]

    await saveBoth(annualData, nextGoals)
    if (ownerProfile?.userId) {
      try {
        await notifyWxwork(ownerProfile.userId, `你被设为目标责任人：${goalForm.title}（${normalizedPeriod}）`)
      } catch (e) {
        console.error('通知发送失败', e)
      }
    }
    if (goalForm.level === 'monthly') setMonthlyPeriod(normalizedPeriod)
    setMemberFilter('')
    setGoalForm(emptyGoalForm)
    setGoalDrawerOpen(false)
  }

  const editOkr = (okr: AnnualOKR) => {
    if (!isOwner) {
      setError('仅负责人可编辑')
      return
    }
    setOkrForm({
      id: okr.id,
      objective: okr.objective,
      category: okr.category,
      owner: okr.owner,
      coOwner: okr.coOwner || '',
      coOwnerId: okr.coOwnerId || '',
      weight: okr.weight,
      progress: okr.progress,
      description: okr.description,
      keyResultsText: okr.keyResults.map((item) => item.title).join('\n'),
      source: okr.source || '系统维护'
    })
    setOkrDrawerOpen(true)
  }

  const editGoal = (goal: GoalItem) => {
    if (!canManageGoals) {
      setError('仅团队成员可编辑')
      return
    }
    setGoalForm({
      id: goal.id,
      title: goal.title,
      description: goal.description,
      level: goal.level,
      period: goal.period,
      owner: goal.owner,
      ownerId: goal.ownerId || '',
      status: goal.status,
      progress: goal.progress,
      priority: goal.priority,
      dueDate: goal.dueDate,
      relatedOkrId: goal.relatedOkrId || '',
      tags: goal.tags.join(', '),
      nextStep: goal.nextStep || '',
      blockers: goal.blockers || '',
      source: goal.source || '系统维护'
    })
    setGoalDrawerOpen(true)
  }

  const removeOkr = async (id: string) => {
    if (!isOwner) {
      setError('仅负责人可删除')
      return
    }
    await saveBoth({ ...annualData, okrs: annualData.okrs.filter((item) => item.id !== id) }, goals)
  }

  const removeGoal = async (id: string) => {
    if (!canManageGoals) {
      setError('仅团队成员可删除')
      return
    }
    await saveBoth(annualData, goals.filter((item) => item.id !== id))
  }

  const canUpdateGoal = (goal: GoalItem) => {
    if (canManageGoals) return true
    if (goal.ownerId && goal.ownerId.toLowerCase() === currentUser.userId.toLowerCase()) return true
    return goal.owner && goal.owner === currentUser.name
  }

  const quickUpdateGoal = async (goal: GoalItem, patch: Partial<GoalItem>) => {
    if (!canUpdateGoal(goal)) return
    setUpdatingGoalId(goal.id)
    setError('')
    setMessage('')
    try {
      const updated = await updateGoal(goal.id, patch)
      setGoals((prev) => prev.map((g) => (g.id === goal.id ? { ...g, ...updated } : g)))
      setMessage('已更新进度')
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新失败')
    } finally {
      setUpdatingGoalId('')
    }
  }

  return (
    <div className="page-shell">
      <section className="page-header-block">
        <div>
          <span className="section-kicker">目标体系</span>
          <h2>目标管理</h2>
          <p>已将年度KPI、3月重点与下周计划导入系统，可直接在此维护、补充、更新进度。</p>
        </div>
        <div className="page-header-actions">
          {canManageGoals ? (
            <>
              <div className="segmented-control" role="group" aria-label="视图切换">
                <button type="button" className={viewMode === 'team' ? 'active' : ''} onClick={() => setViewMode('team')}>团队视图</button>
                <button type="button" className={viewMode === 'mine' ? 'active' : ''} onClick={() => setViewMode('mine')}>我的</button>
              </div>
              {viewMode === 'team' && members.length > 0 && (
                <select value={memberFilter} onChange={(e) => setMemberFilter(e.target.value)} className="select-lite">
                  <option value="">全部责任人</option>
                  {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                </select>
              )}
            </>
          ) : (
            <div className="segmented-control" role="group" aria-label="视图切换">
              <button type="button" className="active" disabled>我的</button>
            </div>
          )}
          <div className="action-group">
            <button className="secondary-btn" disabled={!isOwner} onClick={() => { setOkrForm(emptyOkrForm); setOkrDrawerOpen(true) }}>新增年度OKR</button>
            <button className="primary-btn" disabled={!canManageGoals} onClick={() => { setGoalForm(emptyGoalForm); setGoalDrawerOpen(true) }}>新增执行目标</button>
          </div>
        </div>
      </section>

      {!canManageGoals && (
        <div className="info-banner">
          <strong>只读模式：</strong> 当前账号仅支持查看，请联系团队负责人 {ownerId} 开通维护权限。
        </div>
      )}

      <section className="card-panel">
        <div className="panel-header">
          <div>
            <span className="section-kicker">年度目标</span>
            <h3>OKR总表</h3>
          </div>
          {message && <span className="success-text">{message}</span>}
          {error && <span className="error-text">{error}</span>}
        </div>
        <div className="stack-list">
          {visibleAnnualOkrs.map((okr) => (
            <div key={okr.id} className="stack-item">
              <div className="item-head">
                <div>
                  <strong>{okr.objective}</strong>
                  <p>{okr.category} · 权重 {okr.weight}% · 负责人 {okr.owner}{okr.coOwner ? ` · 协同人 ${okr.coOwner}` : ''}</p>
                </div>
                <div className="inline-actions">
                  <span className="progress-badge">{okr.progress}%</span>
                  <button className="text-btn" disabled={!isOwner} onClick={() => editOkr(okr)}>编辑</button>
                  <button className="text-btn danger" disabled={!isOwner} onClick={() => removeOkr(okr.id)}>删除</button>
                </div>
              </div>
              <p>{okr.description}</p>
              <div className="tag-row">
                {okr.keyResults.map((kr) => <span key={kr.id} className="tag">{kr.title}</span>)}
              </div>
            </div>
          ))}
          {visibleAnnualOkrs.length === 0 && <div className="empty-state">暂无数据</div>}
        </div>
      </section>

      <section className="content-grid two-columns">
        <div className="card-panel">
          <div className="panel-header">
            <div className="panel-title-row">
              <h3>月度重点</h3>
              <span className="panel-meta">{filteredGoals.monthly.length} 项</span>
            </div>
            {monthlyPeriods.length > 0 && (
              <select value={monthlyPeriod} onChange={(e) => setMonthlyPeriod(e.target.value)} className="select-lite">
                {monthlyPeriods.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </div>
          <div className="stack-list">
            {filteredGoals.monthly.map((goal) => (
              <div key={goal.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{goal.title}</strong>
                    <p>{goal.period} · 截止 {goal.dueDate || '待定'} · 责任人 {goal.owner || goal.ownerId || '未指派'}</p>
                  </div>
                  <div className="inline-actions">
                    <span className={`status-badge ${goal.status}`}>{goal.status}</span>
                    <button className="text-btn" disabled={!canManageGoals} onClick={() => editGoal(goal)}>编辑</button>
                    <button className="text-btn danger" disabled={!canManageGoals} onClick={() => removeGoal(goal.id)}>删除</button>
                  </div>
                </div>
                <p>{goal.description}</p>
                <div className="progress-track"><div className="progress-fill" style={{ width: `${goal.progress}%` }} /></div>
                {canUpdateGoal(goal) && (
                  <div className="chips-row">
                    <button type="button" className="pill" disabled={updatingGoalId === goal.id} onClick={() => quickUpdateGoal(goal, { status: 'completed', progress: 100 })}>标记完成</button>
                    <button type="button" className="pill" disabled={updatingGoalId === goal.id} onClick={() => quickUpdateGoal(goal, { status: 'in_progress', progress: Math.max(goal.progress, 50) })}>推进中</button>
                  </div>
                )}
              </div>
            ))}
            {filteredGoals.monthly.length === 0 && <div className="empty-state">暂无数据</div>}
          </div>
        </div>

        <div className="card-panel">
          <div className="panel-header">
            <div className="panel-title-row">
              <h3>周度计划</h3>
              <span className="panel-meta">{filteredGoals.weekly.length} 项</span>
            </div>
            {weeklyPeriods.length > 0 && (
              <select value={weeklyPeriod} onChange={(e) => setWeeklyPeriod(e.target.value)} className="select-lite">
                {weeklyPeriods.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </div>
          <div className="stack-list">
            {filteredGoals.weekly.map((goal) => (
              <div key={goal.id} className="stack-item">
                <div className="item-head">
                  <div>
                    <strong>{goal.title}</strong>
                    <p>{goal.period} · 优先级 {goal.priority} · 责任人 {goal.owner || goal.ownerId || '未指派'}</p>
                  </div>
                  <div className="inline-actions">
                    <span className={`status-badge ${goal.status}`}>{goal.status}</span>
                    <button className="text-btn" disabled={!canManageGoals} onClick={() => editGoal(goal)}>编辑</button>
                    <button className="text-btn danger" disabled={!canManageGoals} onClick={() => removeGoal(goal.id)}>删除</button>
                  </div>
                </div>
                <p>{goal.description}</p>
                {goal.nextStep && <p><strong>下一步：</strong>{goal.nextStep}</p>}
                {goal.blockers && goal.blockers !== '无' && <p className="error-text"><strong>阻塞：</strong>{goal.blockers}</p>}
                {canUpdateGoal(goal) && (
                  <div className="chips-row">
                    <button type="button" className="pill" disabled={updatingGoalId === goal.id} onClick={() => quickUpdateGoal(goal, { status: 'completed', progress: 100 })}>标记完成</button>
                    <button type="button" className="pill" disabled={updatingGoalId === goal.id} onClick={() => quickUpdateGoal(goal, { status: 'in_progress', progress: Math.max(goal.progress, 50) })}>推进中</button>
                  </div>
                )}
              </div>
            ))}
            {filteredGoals.weekly.length === 0 && <div className="empty-state">暂无数据</div>}
          </div>
        </div>
      </section>

      <div className={`side-drawer ${okrDrawerOpen ? 'open' : ''}`} aria-hidden={!okrDrawerOpen}>
        <div className="side-drawer__mask" onClick={() => setOkrDrawerOpen(false)} />
        <div className="drawer-panel">
          <div className="drawer-header">
            <h3>{okrForm.id ? '编辑年度OKR' : '新增年度OKR'}</h3>
            <button type="button" className="drawer-close" onClick={() => setOkrDrawerOpen(false)}>×</button>
          </div>
          <form className="form-panel" onSubmit={handleOkrSubmit}>
            <div className="form-grid">
              <label>
                <span>目标主题</span>
                <input value={okrForm.objective} onChange={(e) => setOkrForm({ ...okrForm, objective: e.target.value })} required disabled={!isOwner} />
              </label>
              <label>
                <span>分类</span>
                <input value={okrForm.category} onChange={(e) => setOkrForm({ ...okrForm, category: e.target.value })} required disabled={!isOwner} />
              </label>
              <label>
                <span>负责人</span>
                <input value={okrForm.owner} onChange={(e) => setOkrForm({ ...okrForm, owner: e.target.value })} required disabled={!isOwner} />
              </label>
              <label>
                <span>协同人</span>
                {members.length > 0 ? (
                  <select
                    value={okrForm.coOwnerId}
                    onChange={(e) => {
                      const uid = e.target.value
                      const profile = members.find((m) => m.userId === uid)
                      setOkrForm({ ...okrForm, coOwnerId: uid, coOwner: profile?.name || '' })
                    }}
                    disabled={!isOwner}
                  >
                    <option value="">不指定</option>
                    {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                  </select>
                ) : (
                  <input value={okrForm.coOwner} onChange={(e) => setOkrForm({ ...okrForm, coOwner: e.target.value })} disabled={!isOwner} />
                )}
              </label>
              <label>
                <span>权重</span>
                <input type="number" min="0" max="100" value={okrForm.weight} onChange={(e) => setOkrForm({ ...okrForm, weight: Number(e.target.value) })} disabled={!isOwner} />
              </label>
              <label>
                <span>当前进度</span>
                <input type="number" min="0" max="100" value={okrForm.progress} onChange={(e) => setOkrForm({ ...okrForm, progress: Number(e.target.value) })} disabled={!isOwner} />
              </label>
              <label>
                <span>来源</span>
                <input value={okrForm.source} onChange={(e) => setOkrForm({ ...okrForm, source: e.target.value })} disabled={!isOwner} />
              </label>
              <label className="full-span">
                <span>目标说明</span>
                <textarea rows={3} value={okrForm.description} onChange={(e) => setOkrForm({ ...okrForm, description: e.target.value })} disabled={!isOwner} />
              </label>
              <label className="full-span">
                <span>关键结果（每行一条）</span>
                <textarea rows={6} value={okrForm.keyResultsText} onChange={(e) => setOkrForm({ ...okrForm, keyResultsText: e.target.value })} required disabled={!isOwner} />
              </label>
            </div>
            <button className="primary-btn" disabled={saving || !isOwner}>{saving ? '保存中...' : '保存年度OKR'}</button>
          </form>
        </div>
      </div>

      <div className={`side-drawer ${goalDrawerOpen ? 'open' : ''}`} aria-hidden={!goalDrawerOpen}>
        <div className="side-drawer__mask" onClick={() => setGoalDrawerOpen(false)} />
        <div className="drawer-panel">
          <div className="drawer-header">
            <h3>{goalForm.id ? '编辑执行目标' : '新增执行目标'}</h3>
            <button type="button" className="drawer-close" onClick={() => setGoalDrawerOpen(false)}>×</button>
          </div>
          <form className="form-panel" onSubmit={handleGoalSubmit}>
            <div className="form-grid">
              <label>
                <span>标题</span>
                <input value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} required disabled={!canManageGoals} />
              </label>
              <label>
                <span>层级</span>
                <select value={goalForm.level} onChange={(e) => setGoalForm({ ...goalForm, level: e.target.value as GoalLevel })} disabled={!canManageGoals}>
                  <option value="monthly">月度目标</option>
                  <option value="weekly">周目标</option>
                  <option value="task">任务项</option>
                </select>
              </label>
              <label>
                <span>周期</span>
                <input value={goalForm.period} onChange={(e) => setGoalForm({ ...goalForm, period: e.target.value })} required disabled={!canManageGoals} />
              </label>
              <label>
                <span>负责人</span>
                {members.length > 0 ? (
                  <select
                    value={goalForm.ownerId}
                    onChange={(e) => {
                      const uid = e.target.value
                      const profile = members.find((m) => m.userId === uid)
                      setGoalForm({ ...goalForm, ownerId: uid, owner: profile?.name || goalForm.owner })
                    }}
                    disabled={!canManageGoals}
                  >
                    <option value="">请选择</option>
                    {members.map((m) => <option key={m.userId} value={m.userId}>{m.name}</option>)}
                  </select>
                ) : (
                  <input value={goalForm.owner} onChange={(e) => setGoalForm({ ...goalForm, owner: e.target.value })} required disabled={!canManageGoals} />
                )}
              </label>
              <label>
                <span>状态</span>
                <select value={goalForm.status} onChange={(e) => setGoalForm({ ...goalForm, status: e.target.value as GoalStatus })} disabled={!canManageGoals}>
                  <option value="not_started">未开始</option>
                  <option value="in_progress">进行中</option>
                  <option value="completed">已完成</option>
                  <option value="blocked">阻塞</option>
                </select>
              </label>
              <label>
                <span>优先级</span>
                <select value={goalForm.priority} onChange={(e) => setGoalForm({ ...goalForm, priority: e.target.value as Priority })} disabled={!canManageGoals}>
                  <option value="high">高</option>
                  <option value="medium">中</option>
                  <option value="low">低</option>
                </select>
              </label>
              <label>
                <span>进度</span>
                <input type="number" min="0" max="100" value={goalForm.progress} onChange={(e) => setGoalForm({ ...goalForm, progress: Number(e.target.value) })} disabled={!canManageGoals} />
              </label>
              <label>
                <span>截止日期</span>
                <input type="date" value={goalForm.dueDate} onChange={(e) => setGoalForm({ ...goalForm, dueDate: e.target.value })} disabled={!canManageGoals} />
              </label>
              <label className="full-span">
                <span>关联年度OKR</span>
                <select value={goalForm.relatedOkrId} onChange={(e) => setGoalForm({ ...goalForm, relatedOkrId: e.target.value })} disabled={!canManageGoals}>
                  <option value="">不关联</option>
                  {annualData.okrs.map((okr) => <option key={okr.id} value={okr.id}>{okr.objective}</option>)}
                </select>
              </label>
              <label className="full-span">
                <span>目标描述</span>
                <textarea rows={3} value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} disabled={!canManageGoals} />
              </label>
              <label className="full-span">
                <span>标签（逗号分隔）</span>
                <input value={goalForm.tags} onChange={(e) => setGoalForm({ ...goalForm, tags: e.target.value })} disabled={!canManageGoals} />
              </label>
              <label className="full-span">
                <span>下一步动作</span>
                <textarea rows={2} value={goalForm.nextStep} onChange={(e) => setGoalForm({ ...goalForm, nextStep: e.target.value })} disabled={!canManageGoals} />
              </label>
              <label className="full-span">
                <span>阻塞说明</span>
                <textarea rows={2} value={goalForm.blockers} onChange={(e) => setGoalForm({ ...goalForm, blockers: e.target.value })} placeholder="若状态为阻塞，请给出原因与依赖" disabled={!canManageGoals} />
              </label>
            </div>
            <button className="primary-btn" disabled={saving || !canManageGoals}>{saving ? '保存中...' : '保存执行目标'}</button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Goals
